const MAX_BYTES = 10 * 1024 * 1024;
const MAX_EDGE = 12000;

// Pulls the EXIF APP1 segment out of a JPEG so it survives canvas re-encoding
// (camera/lens/ISO data powers the Behind the Lens AI).
async function extractExifSegment(file) {
  if (file.type !== "image/jpeg") return null;
  const buf = new Uint8Array(await file.slice(0, 256 * 1024).arrayBuffer());
  if (buf[0] !== 0xff || buf[1] !== 0xd8) return null;
  let off = 2;
  while (off + 4 < buf.length) {
    if (buf[off] !== 0xff) break;
    const marker = buf[off + 1];
    const size = (buf[off + 2] << 8) | buf[off + 3];
    if (
      marker === 0xe1 &&
      buf[off + 4] === 0x45 && buf[off + 5] === 0x78 &&
      buf[off + 6] === 0x69 && buf[off + 7] === 0x66
    ) {
      if (off + 2 + size > buf.length) return null;
      return buf.slice(off, off + 2 + size);
    }
    if (marker === 0xda) break;
    off += 2 + size;
  }
  return null;
}

function insertExif(jpegBytes, exifSegment) {
  const out = new Uint8Array(jpegBytes.length + exifSegment.length);
  out.set(jpegBytes.slice(0, 2), 0);
  out.set(exifSegment, 2);
  out.set(jpegBytes.slice(2), 2 + exifSegment.length);
  return out;
}

/**
 * Keeps the image untouched if it fits the upload limits. Otherwise shrinks it
 * just enough (quality first, then dimensions) to fit - maximum resolution kept.
 * Returns { file, resized, from? }.
 */
export async function autoResizeImage(file, { maxBytes = MAX_BYTES, maxEdge = MAX_EDGE } = {}) {
  if (!file || !file.type?.startsWith("image/")) return { file, resized: false };
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "none" });
  } catch {
    return { file, resized: false };
  }
  const { width, height } = bitmap;
  if (file.size <= maxBytes && width <= maxEdge && height <= maxEdge) {
    bitmap.close();
    return { file, resized: false };
  }

  const toBlob = (canvas, q) => new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
  const draw = (scale) => {
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    return canvas;
  };

  let scale = Math.min(1, maxEdge / Math.max(width, height));
  let blob = null;
  outer: for (let attempt = 0; attempt < 6; attempt += 1) {
    const canvas = draw(scale);
    for (const q of [0.92, 0.87, 0.82]) {
      // eslint-disable-next-line no-await-in-loop
      blob = await toBlob(canvas, q);
      if (blob && blob.size <= maxBytes) break outer;
    }
    blob = null;
    scale *= 0.85;
  }
  bitmap.close();
  if (!blob) return { file, resized: false };

  let bytes = new Uint8Array(await blob.arrayBuffer());
  try {
    const exif = await extractExifSegment(file);
    if (exif && bytes.length + exif.length <= maxBytes) {
      bytes = insertExif(bytes, exif);
    }
  } catch {
    // EXIF copy is best-effort
  }
  const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
  return {
    file: new File([bytes], name, { type: "image/jpeg" }),
    resized: true,
    from: { width, height, bytes: file.size },
  };
}

export const formatMB = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)}MB`;
